import { readU16LE, readU32LE } from "./byte-reading";

const OLE2_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] as const;

export type Ole2DirectoryKind = "email" | "word" | "excel";

export interface Ole2DirEntry {
  name: string | null;
  left: number;
  right: number;
  child: number;
}

export type Ole2RangeReader = (
  offset: number,
  length: number,
) => Promise<Uint8Array>;

const FREESECT = 0xffffffff;
const ENDOFCHAIN = 0xfffffffe;
const MAXREGSECT = 0xfffffffa;
const HEADER_SIZE = 512;
const DIR_ENTRY_SIZE = 128;
const MAX_HEADER_DIFAT = 109;
const MAX_NAME_BYTES = 64;
const MAX_DIFAT_SECTORS = 256;
const MAX_DIRECTORY_SECTORS = 4096;
/** Remote reads fetch aligned windows so adjacent sectors share one read. */
const RANGE_READ_WINDOW_BYTES = 64 * 1024;
/** At most 256 windows (16 MB) of remote reads per detection. */
const MAX_ASYNC_RANGE_READS = 256;
const NOSTREAM = FREESECT;
const MSG_PROPERTIES = "__properties_version1.0";
const MSG_SUBSTG_PREFIX = "__substg1.0_";
const WORD_STREAM = "WordDocument";
const EXCEL_WORKBOOK = "Workbook";
const EXCEL_BOOK = "Book";

interface CfbHeader {
  sectorSize: number;
  fatSectorCount: number;
  firstDirectorySector: number;
  firstDifatSector: number;
  headerDifat: readonly number[];
}

export function hasOle2Magic(bytes: Uint8Array): boolean {
  if (bytes.length < OLE2_MAGIC.length) return false;
  for (let i = 0; i < OLE2_MAGIC.length; i++) {
    if (bytes[i] !== OLE2_MAGIC[i]) return false;
  }
  return true;
}

function isRegularSector(sectorId: number): boolean {
  return sectorId <= MAXREGSECT;
}

function isEndOfChain(sectorId: number): boolean {
  return sectorId === ENDOFCHAIN;
}

function sliceBytes(
  bytes: Uint8Array,
  offset: number,
  length: number,
): Uint8Array | null {
  if (offset < 0 || length < 0) return null;
  if (offset + length > bytes.length) return null;
  return bytes.subarray(offset, offset + length);
}

function sectorByteOffset(sectorSize: number, sectorId: number): number | null {
  if (!isRegularSector(sectorId)) return null;
  return (sectorId + 1) * sectorSize;
}

function maxFatSectorsForFile(fileSize: number, sectorSize: number): number {
  const maxOffset = fileSize - sectorSize;
  if (maxOffset < sectorSize) return 0;
  const maxSectorId = Math.floor(maxOffset / sectorSize) - 1;
  if (maxSectorId < 0) return 0;
  const entriesPerFat = sectorSize / 4;
  return Math.floor(maxSectorId / entriesPerFat) + 1;
}

function parseSectorSize(bytes: Uint8Array): number | null {
  const byteOrder = readU16LE(bytes, 0x1c);
  const sectorShift = readU16LE(bytes, 0x1e);
  if (byteOrder !== 0xfffe) return null;
  if (sectorShift !== 9 && sectorShift !== 12) return null;
  return 1 << sectorShift;
}

function readCfbChainFields(bytes: Uint8Array): {
  fatSectorCount: number;
  firstDirectorySector: number;
  firstDifatSector: number;
} | null {
  const fatSectorCount = readU32LE(bytes, 0x2c);
  const firstDirectorySector = readU32LE(bytes, 0x30);
  const firstDifatSector = readU32LE(bytes, 0x44);
  if (
    fatSectorCount == null ||
    firstDirectorySector == null ||
    firstDifatSector == null ||
    fatSectorCount === 0
  ) {
    return null;
  }
  return { fatSectorCount, firstDirectorySector, firstDifatSector };
}

function readHeaderDifat(bytes: Uint8Array): number[] | null {
  const headerDifat: number[] = [];
  for (let i = 0; i < MAX_HEADER_DIFAT; i++) {
    const id = readU32LE(bytes, 0x4c + i * 4);
    if (id == null) return null;
    headerDifat.push(id);
  }
  return headerDifat;
}

function parseCfbHeader(bytes: Uint8Array): CfbHeader | null {
  if (!hasOle2Magic(bytes) || bytes.length < HEADER_SIZE) return null;
  const sectorSize = parseSectorSize(bytes);
  const chain = readCfbChainFields(bytes);
  const headerDifat = readHeaderDifat(bytes);
  if (sectorSize == null || chain == null || headerDifat == null) return null;
  return { sectorSize, headerDifat, ...chain };
}

function utf16LeName(entry: Uint8Array, byteLength: number): string | null {
  if (byteLength < 2 || byteLength > MAX_NAME_BYTES || byteLength % 2 !== 0) {
    return null;
  }
  const charCount = byteLength / 2 - 1;
  let name = "";
  for (let i = 0; i < charCount; i++) {
    const lo = entry[i * 2];
    const hi = entry[i * 2 + 1];
    if (lo === undefined || hi === undefined) return null;
    const code = lo | (hi << 8);
    if (code === 0) return name;
    name += String.fromCharCode(code);
  }
  return name;
}

function directoryEntryName(entry: Uint8Array): string | null {
  if (entry.length < DIR_ENTRY_SIZE) return null;
  const type = entry[0x42];
  if (type !== 1 && type !== 2 && type !== 5) return null;
  const nameBytes = readU16LE(entry, 0x40);
  if (nameBytes == null) return null;
  const name = utf16LeName(entry, nameBytes);
  if (name == null || name.length === 0) return null;
  return name;
}

function parseDirEntry(entry: Uint8Array): Ole2DirEntry | null {
  if (entry.length < DIR_ENTRY_SIZE) return null;
  const left = readU32LE(entry, 0x44);
  const right = readU32LE(entry, 0x48);
  const child = readU32LE(entry, 0x4c);
  if (left == null || right == null || child == null) return null;
  return { name: directoryEntryName(entry), left, right, child };
}

function emptyDirEntry(): Ole2DirEntry {
  return { name: null, left: NOSTREAM, right: NOSTREAM, child: NOSTREAM };
}

function collectDirectoryEntries(sector: Uint8Array): Ole2DirEntry[] {
  const entries: Ole2DirEntry[] = [];
  for (
    let offset = 0;
    offset + DIR_ENTRY_SIZE <= sector.length;
    offset += DIR_ENTRY_SIZE
  ) {
    entries.push(
      parseDirEntry(sector.subarray(offset, offset + DIR_ENTRY_SIZE)) ??
        emptyDirEntry(),
    );
  }
  return entries;
}

function isDirectorySid(id: number, count: number): boolean {
  return id !== NOSTREAM && id < count;
}

export function rootChildNames(entries: readonly Ole2DirEntry[]): string[] {
  const names: string[] = [];
  const root = entries[0];
  if (root == null) return names;
  const stack = [root.child];
  const seen = new Set<number>();
  while (stack.length > 0) {
    const id = stack.pop();
    if (id == null || !isDirectorySid(id, entries.length) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const entry = entries[id];
    if (entry == null) continue;
    if (entry.name != null) names.push(entry.name);
    stack.push(entry.left, entry.right);
  }
  return names;
}

function kindFromDirectoryEntries(
  entries: Ole2DirEntry[] | null,
): Ole2DirectoryKind | null {
  if (entries == null) return null;
  return classifyOle2DirectoryNames(rootChildNames(entries));
}

export function classifyOle2DirectoryNames(
  names: readonly string[],
): Ole2DirectoryKind | null {
  for (const name of names) {
    if (name === MSG_PROPERTIES || name.startsWith(MSG_SUBSTG_PREFIX)) {
      return "email";
    }
  }
  for (const name of names) {
    if (name === WORD_STREAM) return "word";
  }
  for (const name of names) {
    if (name === EXCEL_WORKBOOK || name === EXCEL_BOOK) return "excel";
  }
  return null;
}

function bytesFromHeadOrTail(
  head: Uint8Array,
  tail: Uint8Array,
  fileSize: number,
  offset: number,
  length: number,
): Uint8Array | null {
  const fromHead = sliceBytes(head, offset, length);
  if (fromHead != null) return fromHead;
  if (tail.length === 0 || fileSize < tail.length) return null;
  const tailStart = fileSize - tail.length;
  if (offset < tailStart) return null;
  return sliceBytes(tail, offset - tailStart, length);
}

function readExactRange(
  bytes: Uint8Array | null,
  length: number,
): Uint8Array | null {
  if (bytes == null || bytes.length < length) return null;
  return bytes.length === length ? bytes : bytes.subarray(0, length);
}

/**
 * A byte range the directory walk needs. The walk is written once as a
 * generator that yields these requests; a sync driver answers them from an
 * in-memory buffer and an async driver from ranged storage reads.
 */
interface ByteRequest {
  offset: number;
  length: number;
}

type ByteWalk<T> = Generator<ByteRequest, T, Uint8Array | null>;

function* readSector(
  header: CfbHeader,
  fileSize: number,
  sectorId: number,
): ByteWalk<Uint8Array | null> {
  const offset = sectorByteOffset(header.sectorSize, sectorId);
  if (offset == null || offset + header.sectorSize > fileSize) return null;
  const bytes = yield { offset, length: header.sectorSize };
  return readExactRange(bytes, header.sectorSize);
}

function headerDifatEntry(header: CfbHeader, fatIndex: number): number | null {
  const id = header.headerDifat[fatIndex];
  if (id == null || id === FREESECT || !isRegularSector(id)) return null;
  return id;
}

function fatIndexForSector(header: CfbHeader, sectorId: number): number {
  return Math.floor(sectorId / (header.sectorSize / 4));
}

function fatOffsetInSector(header: CfbHeader, sectorId: number): number {
  return (sectorId % (header.sectorSize / 4)) * 4;
}

export function readDifatChainStep(
  sector: Uint8Array,
  remaining: number,
  entriesPerDifat: number,
): { id: number } | { next: number } | null {
  if (remaining < entriesPerDifat) {
    const id = readU32LE(sector, remaining * 4);
    if (id == null || id === FREESECT || !isRegularSector(id)) return null;
    return { id };
  }
  const next = readU32LE(sector, entriesPerDifat * 4);
  if (next == null) return null;
  return { next };
}

function canVisitDifatSector(
  visited: ReadonlySet<number>,
  difatSid: number,
): boolean {
  return (
    isRegularSector(difatSid) &&
    !visited.has(difatSid) &&
    visited.size < MAX_DIFAT_SECTORS
  );
}

function* resolveDifatFatSid(
  header: CfbHeader,
  fileSize: number,
  fatIndex: number,
): ByteWalk<number | null> {
  if (fatIndex < MAX_HEADER_DIFAT) return headerDifatEntry(header, fatIndex);
  let remaining = fatIndex - MAX_HEADER_DIFAT;
  let difatSid = header.firstDifatSector;
  const entriesPerDifat = header.sectorSize / 4 - 1;
  const visited = new Set<number>();
  while (canVisitDifatSector(visited, difatSid)) {
    visited.add(difatSid);
    const sector = yield* readSector(header, fileSize, difatSid);
    const step =
      sector && readDifatChainStep(sector, remaining, entriesPerDifat);
    if (step == null) return null;
    if ("id" in step) return step.id;
    remaining -= entriesPerDifat;
    difatSid = step.next;
  }
  return null;
}

function* nextFatEntry(
  header: CfbHeader,
  fileSize: number,
  cache: Map<number, Uint8Array>,
  sectorId: number,
): ByteWalk<number | null> {
  const fatIndex = fatIndexForSector(header, sectorId);
  if (fatIndex >= header.fatSectorCount) return null;
  let fatSector = cache.get(fatIndex);
  if (fatSector == null) {
    const fatSid = yield* resolveDifatFatSid(header, fileSize, fatIndex);
    if (fatSid == null) return null;
    const loaded = yield* readSector(header, fileSize, fatSid);
    if (loaded == null) return null;
    cache.set(fatIndex, loaded);
    fatSector = loaded;
  }
  return readU32LE(fatSector, fatOffsetInSector(header, sectorId));
}

function headerIfUsable(head: Uint8Array, fileSize: number): CfbHeader | null {
  const header = parseCfbHeader(head);
  if (header == null) return null;
  if (fileSize < header.sectorSize * 2) return null;
  const maxFat = maxFatSectorsForFile(fileSize, header.sectorSize);
  if (maxFat === 0 || header.fatSectorCount > maxFat) return null;
  if (!isRegularSector(header.firstDirectorySector)) return null;
  return header;
}

/**
 * Follow the directory sector chain through the FAT, collecting every entry.
 * Bounded by {@link MAX_DIRECTORY_SECTORS} and a visited set, so a cyclic or
 * truncated chain yields null instead of looping or guessing.
 */
function* walkDirectoryEntries(
  header: CfbHeader,
  fileSize: number,
): ByteWalk<Ole2DirEntry[] | null> {
  const entries: Ole2DirEntry[] = [];
  const visited = new Set<number>();
  const fatCache = new Map<number, Uint8Array>();
  let sectorId = header.firstDirectorySector;
  for (let i = 0; i < MAX_DIRECTORY_SECTORS; i++) {
    if (!isRegularSector(sectorId)) return null;
    if (visited.has(sectorId)) return null;
    visited.add(sectorId);
    const sector = yield* readSector(header, fileSize, sectorId);
    if (sector == null) return null;
    entries.push(...collectDirectoryEntries(sector));
    const next = yield* nextFatEntry(header, fileSize, fatCache, sectorId);
    if (next == null) return null;
    if (isEndOfChain(next)) return entries;
    sectorId = next;
  }
  return null;
}

function runWalkSync<T>(
  walk: ByteWalk<T>,
  getBytes: (offset: number, length: number) => Uint8Array | null,
): T {
  let step = walk.next(null);
  while (!step.done) {
    step = walk.next(getBytes(step.value.offset, step.value.length));
  }
  return step.value;
}

async function runWalkAsync<T>(
  walk: ByteWalk<T>,
  getBytes: (offset: number, length: number) => Promise<Uint8Array | null>,
): Promise<T> {
  let step = walk.next(null);
  while (!step.done) {
    step = walk.next(await getBytes(step.value.offset, step.value.length));
  }
  return step.value;
}

interface RangeReadBudget {
  used: number;
  max: number;
}

/**
 * Read the aligned window that holds `offset`, once per window. Sectors are
 * aligned to their size and windows to a multiple of it, so a sector never
 * spans two windows; a request that would is read directly instead.
 */
async function readWindowed(
  readRange: Ole2RangeReader,
  fileSize: number,
  windows: Map<number, Uint8Array>,
  budget: RangeReadBudget,
  offset: number,
  length: number,
): Promise<Uint8Array | null> {
  const windowIndex = Math.floor(offset / RANGE_READ_WINDOW_BYTES);
  const windowStart = windowIndex * RANGE_READ_WINDOW_BYTES;
  const spansWindows = offset + length > windowStart + RANGE_READ_WINDOW_BYTES;
  let window = spansWindows ? undefined : windows.get(windowIndex);
  if (window == null) {
    if (budget.used >= budget.max) return null;
    budget.used += 1;
    if (spansWindows) return readRange(offset, length);
    const windowLength = Math.min(
      RANGE_READ_WINDOW_BYTES,
      fileSize - windowStart,
    );
    window = await readRange(windowStart, windowLength);
    windows.set(windowIndex, window);
  }
  return sliceBytes(window, offset - windowStart, length);
}

function asyncOle2Bytes(
  head: Uint8Array,
  tail: Uint8Array,
  fileSize: number,
  readRange: Ole2RangeReader | undefined,
  budget: RangeReadBudget,
): (offset: number, length: number) => Promise<Uint8Array | null> {
  const windows = new Map<number, Uint8Array>();
  return async (offset, length) => {
    const local = bytesFromHeadOrTail(head, tail, fileSize, offset, length);
    if (local != null) return local;
    if (readRange == null) return null;
    if (offset + length > fileSize) return null;
    return readWindowed(readRange, fileSize, windows, budget, offset, length);
  };
}

export function detectOle2DirectoryKindFromBytes(
  bytes: Uint8Array,
): Ole2DirectoryKind | null {
  const header = headerIfUsable(bytes, bytes.length);
  if (header == null) return null;
  return kindFromDirectoryEntries(
    runWalkSync(walkDirectoryEntries(header, bytes.length), (offset, length) =>
      sliceBytes(bytes, offset, length),
    ),
  );
}

export async function detectOle2DirectoryKind(input: {
  head: Uint8Array;
  tail?: Uint8Array;
  fileSize: number;
  readRange?: Ole2RangeReader;
  maxRangeReads?: number;
}): Promise<Ole2DirectoryKind | null> {
  const header = headerIfUsable(input.head, input.fileSize);
  if (header == null) return null;
  return kindFromDirectoryEntries(
    await runWalkAsync(
      walkDirectoryEntries(header, input.fileSize),
      asyncOle2Bytes(
        input.head,
        input.tail ?? new Uint8Array(),
        input.fileSize,
        input.readRange,
        { used: 0, max: input.maxRangeReads ?? MAX_ASYNC_RANGE_READS },
      ),
    ),
  );
}
